import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path, Circle } from 'react-native-svg';
import { COLORS, FONTS } from '../../../constants/config';
import { Organization } from '../../../types';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getTextAlign } from '../../../utils/rtl';
import { useAutoTranslate } from '../../../hooks/useAutoTranslate';
import { formatPct } from '../../../utils/formatPercentage';
import { commitmentColor } from '../../../utils/metricColors';

import { tokens } from '@design';
// Building Icon
const BuildingIcon = ({ size = 24, color = tokens.colors.semantic.general["Main Color"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 21V7L13 3V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 21V11L13 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 9V9.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 12V12.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 15V15.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 18V18.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Chevron Left Icon
const ChevronLeftIcon = ({ size = 20, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Progress Circle Component
const ProgressCircle = ({
  percentage,
  size = 60,
  strokeWidth = 6,
  trackColor = tokens.colors.raw.gray["200"],
}: {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  trackColor?: string;
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;

  // Unified commitment scheme: >=80 green, 50–79 orange, <50 red.
  const getColor = () => commitmentColor(safePercentage);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        {/* Background Circle */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress Circle */}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getColor()}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </Svg>
      <Text style={[styles.progressText, { color: getColor() }]}>
        %{formatPct(safePercentage)}
      </Text>
    </View>
  );
};

interface OrganizationCardProps {
  organization: Organization;
  onPress?: () => void;
}

export default function OrganizationCard({ organization, onPress }: OrganizationCardProps) {
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const visionAchievement = Number(((organization.stage_actual_progress ?? 0) * 100).toFixed(2));
  const commitmentIndex = organization.commitment_index ?? 0;
  const goalsCount = organization.strategic_goals_count ?? 0;
  const kpisCount = organization.kpis_count ?? 0;

  // Theme colors
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    orgName: colors.textPrimary,
    statLabel: colors.textMuted,
    countContainer: colors.backgroundAlt,
    countValue: colors.textPrimary,
    divider: isDark ? colors.border : tokens.colors.raw.gray["100"],
    progressTrack: isDark ? '#4B5563' : tokens.colors.raw.gray["200"],
    iconChevron: colors.textMuted,
    iconContainer: isDark ? colors.primaryLight : tokens.colors.raw.amber["100"],
  };

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {/* Header */}
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: themeColors.iconContainer }]}>
          <BuildingIcon size={24} color={colors.primary} />
        </View>
        <View style={styles.headerContent}>
          <Text style={[styles.orgName, { color: themeColors.orgName }]} numberOfLines={2}>{translateOrg(organization.name)}</Text>
        </View>
        <ChevronLeftIcon size={20} color={themeColors.iconChevron} />
      </View>

      {/* Stats Row */}
      <View style={[styles.statsRow, { borderTopColor: themeColors.divider }]}>
        {/* Vision Achievement */}
        <View style={styles.statItem}>
          <ProgressCircle percentage={visionAchievement} size={56} strokeWidth={5} trackColor={themeColors.progressTrack} />
          <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>{t('dashboard.visionAchievement')}</Text>
        </View>

        {/* Commitment Index */}
        <View style={styles.statItem}>
          <ProgressCircle percentage={commitmentIndex} size={56} strokeWidth={5} trackColor={themeColors.progressTrack} />
          <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>{t('dashboard.commitmentIndex')}</Text>
        </View>

        {/* Goals Count */}
        <View style={styles.statItem}>
          <View style={[styles.countContainer, { backgroundColor: themeColors.countContainer }]}>
            <Text style={[styles.countValue, { color: themeColors.countValue }]}>{goalsCount}</Text>
          </View>
          <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>{t('dashboard.goals')}</Text>
        </View>

        {/* KPIs Count */}
        <View style={styles.statItem}>
          <View style={[styles.countContainer, { backgroundColor: themeColors.countContainer }]}>
            <Text style={[styles.countValue, { color: themeColors.countValue }]}>{kpisCount}</Text>
          </View>
          <Text style={[styles.statLabel, { color: themeColors.statLabel }]}>{t('dashboard.kpis')}</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  card: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.gray["200"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["3"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["4"],
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-10"],
    backgroundColor: tokens.colors.raw.red["50"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerContent: {
    flex: 1,
    marginEnd: tokens.spacing.absolute["3"],
    marginStart: tokens.spacing.absolute["2"],
  },
  orgName: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.bold,
    color: tokens.colors.raw.gray["900"],
    textAlign: getTextAlign(isRTL),
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingTop: tokens.spacing.absolute["3"],
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: tokens.colors.raw.gray["100"],
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    fontSize: tokens.typography.paragraph["paragraph caption font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
    marginTop: tokens.spacing.absolute["1_5"],
    textAlign: 'center',
  },
  progressText: {
    position: 'absolute',
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.numbers,
    fontWeight: '600',
  },
  countContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: tokens.colors.raw.gray["100"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  countValue: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    color: tokens.colors.raw.gray["700"],
  },
});

const styles = getStyles(true);
